import type { Membership } from '../api/types.js';

export type EffectiveRole = Membership['role'];

// Highest → lowest precedence per `feedback_validator_role` (CLAUDE.md
// + REQ-FE-019). The contract Membership.role enum doesn't list a
// dedicated "validator" value — `ap_approver` is the closest semantic
// equivalent for the "non-finance employee who only validates one
// invoice" use case. `viewer` is even more read-only than that.
const ORDER: EffectiveRole[] = ['group_admin', 'ap_admin', 'ap_clerk', 'ap_approver', 'viewer'];

const RANK: Record<EffectiveRole, number> = ORDER.reduce(
  (acc, r, i) => {
    acc[r] = ORDER.length - i;
    return acc;
  },
  {} as Record<EffectiveRole, number>,
);

/**
 * Returns the highest-precedence role across the caller's memberships.
 * `null` when the caller has no memberships at all (e.g. a validator
 * invited by email who hasn't been provisioned a membership yet).
 */
export function getMaxRole(memberships: Membership[]): EffectiveRole | null {
  if (memberships.length === 0) return null;
  let best: EffectiveRole | null = null;
  let bestRank = -1;
  for (const m of memberships) {
    const r = RANK[m.role];
    if (r > bestRank) {
      bestRank = r;
      best = m.role;
    }
  }
  return best;
}

/**
 * True when every membership the caller holds is `ap_approver` (our
 * mapping of the spec's "validator" role). Empty memberships → false
 * because we can't determine intent in that case — the dashboard hook
 * fans out per tenant so an empty list just means an empty dashboard.
 */
export function isValidatorOnly(memberships: Membership[]): boolean {
  if (memberships.length === 0) return false;
  return memberships.every((m) => m.role === 'ap_approver');
}

/**
 * True when the caller has at least one `group_admin` membership on
 * any tenant. Used to gate the global `/admin/members` screen and the
 * sidebar "Administration" entry.
 */
export function isGroupAdmin(memberships: Membership[]): boolean {
  return memberships.some((m) => m.role === 'group_admin');
}
